import React, { useContext, useEffect, useRef } from "react";
import { Box, ButtonGroup, Button, SelectChangeEvent } from "@mui/material";
import { PivotViewConfig } from "../../entities/pivot_view_config";
import { DatePivotParams } from "../../entities/date_pivot_params";
import { SearchableDropdown } from "../../components/searchable_dropdown";
import { MultiSelect } from "../../components/multi_select";
import {
  ParsedParquetData,
  isChunkedParsedData,
} from "../../utils/parquet";
import { SupplyWalkEditsContext } from "../../contexts/supply_walk_edits/provider";
import { useCustomer } from "../../contexts/app/provider";
import {
  NEEDS_REVIEW_NEW_POS_TO_PLACE,
  NEEDS_REVIEW_OPEN_PO_MODIFICATIONS,
} from "./default_config";

// Sanitize a status string into the count-column suffix the backend emits.
// "Needs Review: New POs to Place" -> "needs_review_new_pos_to_place"
const sanitizeStatus = (s: string): string =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/_+$/, "");

// Display labels differ from the canonical backend values only for GoodChop —
// users see an apostrophe form, the backend sees the apostrophe-free form.
const STATUS_LABEL_MAP: Record<string, string> = {
  [NEEDS_REVIEW_NEW_POS_TO_PLACE]: "Needs Review: New PO's",
  [NEEDS_REVIEW_OPEN_PO_MODIFICATIONS]: "Existing PO's to Modify",
};

export const SupplyWalkEditableCustomControls = <
  T extends DatePivotParams = DatePivotParams,
>({
  extraActions,
  viewControllerConfig,
  params,
  setParams,
  isLoading,
  isChartView,
  setIsChartView,
  sortConfig,
  setSortConfig,
  dateHierarchyColumns,
  segmentColumns,
  measureColumns,
  filterColumns,
  columnSchema,
  parsedData,
}: {
  /** Rendered beside the status tabs; used for the GoodChop "Add SKU" button. */
  extraActions?: React.ReactNode;
  viewControllerConfig: PivotViewConfig<T>;
  params: T;
  setParams: (params: T) => void;
  isLoading: boolean;
  isChartView?: boolean;
  setIsChartView?: React.Dispatch<React.SetStateAction<boolean>>;
  sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
  setSortConfig: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  dateHierarchyColumns: {
    name: string;
    caption: string;
    min: string;
    max: string;
  }[];
  segmentColumns: { name: string; caption: string }[];
  measureColumns: { name: string; caption: string }[];
  filterColumns: { name: string; caption: string; unique_values: string[] }[];
  columnSchema: Record<
    string,
    {
      dtype: string;
      min?: string;
      max?: string;
      unique_values?: string[];
    }
  >;
  parsedData?: ParsedParquetData | null;
}) => {
  const { customerSettings } = useCustomer();
  // Server-resolved customer id, not the root-only selector value — see
  // SupplyWalkEditableContainer.
  const isGoodchop = customerSettings?.id === "goodchop";
  const availableStatuses = isGoodchop
    ? [
        "Delivered",
        "Open",
        NEEDS_REVIEW_OPEN_PO_MODIFICATIONS,
        NEEDS_REVIEW_NEW_POS_TO_PLACE,
        "Approved",
      ]
    : ["Delivered", "Open", "Needs Review", "Approved"];
  const filterLabels = isGoodchop
    ? {
        poId: "PO Number",
        sku: "SKU",
        skuName: "Description",
        location: "Location",
        supplier: "Supplier",
        reportingAttribution: "Reporting Attribute",
      }
    : {
        poId: "po id",
        sku: "sku",
        skuName: "sku name",
        location: "location",
        supplier: "supplier",
        reportingAttribution: "reporting attribute",
      };

  // Get context to update approved row count
  const supplyWalkEdits = useContext(SupplyWalkEditsContext);

  // Cache the last successfully extracted counts so we can keep showing
  // them while a refresh is in flight. Without this, every save triggers
  // a brief LOADING state where parsedData is null, getStatusCounts would
  // return all zeros, and every tab label would visibly jump to (0)
  // before snapping back to the new value a beat later.
  const lastCountsRef = useRef<Record<string, number> | null>(null);

  // Extract status counts from parsed data (full table only; chunked path has no arrowTable).
  // Count column names follow the sanitize(status) + "_count" convention, so the set of
  // available counts is driven by availableStatuses.
  const getStatusCounts = (): Record<string, number> => {
    const emptyCounts: Record<string, number> = {};
    for (const s of availableStatuses) {
      emptyCounts[`${sanitizeStatus(s)}_count`] = 0;
    }

    if (
      !parsedData ||
      isChunkedParsedData(parsedData) ||
      !parsedData.arrowTable
    ) {
      // Loading / chunked-mode fallback: prefer the last good counts so
      // the tab labels don't flicker to (0) on every save-driven refresh.
      return lastCountsRef.current ?? emptyCounts;
    }

    try {
      const allRows = parsedData.arrowTable.toArray() as Record<string, unknown>[];
      const countRow =
        allRows.find(
          (row: Record<string, unknown>) => row.purchase_order_id === "DUMMY"
        ) ||
        allRows[0] ||
        {};

      const counts: Record<string, number> = {};
      for (const s of availableStatuses) {
        const key = `${sanitizeStatus(s)}_count`;
        counts[key] = Number((countRow as Record<string, unknown>)[key]) || 0;
      }
      lastCountsRef.current = counts;
      return counts;
    } catch (error) {
      console.warn("Error extracting status counts:", error);
      return lastCountsRef.current ?? emptyCounts;
    }
  };

  const baseStatusCounts = getStatusCounts();
  const approvedCount = baseStatusCounts.approved_count ?? 0;

  // Update approved row count in context when data changes (initial load only)
  useEffect(() => {
    if (supplyWalkEdits?.setApprovedRowCount) {
      supplyWalkEdits.setApprovedRowCount(approvedCount);
    }
  }, [approvedCount, supplyWalkEdits]);

  // Apply optimistic deltas on top of the server-baked DUMMY counts so the
  // tab labels react instantly when a user approves a row. The server-side
  // refresh after save eventually catches up; we drop the deltas as soon as
  // a fresh parsedData arrives (effect below).
  const pendingApprovalDeltas = supplyWalkEdits?.pendingApprovalDeltas;
  let statusCounts: Record<string, number> = baseStatusCounts;
  if (pendingApprovalDeltas && pendingApprovalDeltas.size > 0) {
    const adjusted: Record<string, number> = { ...baseStatusCounts };
    for (const sourceStatus of pendingApprovalDeltas.values()) {
      const sourceKey = `${sanitizeStatus(sourceStatus)}_count`;
      if (sourceKey in adjusted) {
        adjusted[sourceKey] = Math.max(0, (adjusted[sourceKey] || 0) - 1);
      }
    }
    adjusted.approved_count =
      (adjusted.approved_count || 0) + pendingApprovalDeltas.size;
    statusCounts = adjusted;
  }

  // Drop optimistic deltas only when a save has completed since we last
  // cleared (signalled by bumpReloadVersion advancing) AND a fresh
  // parsedData has landed for that bumped subscription. Clearing on every
  // parsedData change is too eager: a tab switch (e.g. → Approved) triggers
  // a refetch with the OLD reloadVersion, parsedData updates with stale
  // server state, and we'd wipe the delta even though the save hasn't
  // written approved_pos.parquet yet — the count would visibly drop to 0.
  const reloadVersion = supplyWalkEdits?.reloadVersion ?? 0;
  const lastClearedVersionRef = useRef(0);
  useEffect(() => {
    if (!supplyWalkEdits?.clearAllPendingApprovals) return;
    if (!parsedData) return;
    if (reloadVersion <= lastClearedVersionRef.current) return;
    lastClearedVersionRef.current = reloadVersion;
    supplyWalkEdits.clearAllPendingApprovals();
  }, [parsedData, reloadVersion, supplyWalkEdits]);

  // Get current status filter value
  const currentStatusFilter = params.filters.find(
    (filter) => filter[0] === "status"
  );
  const currentStatus = currentStatusFilter
    ? (currentStatusFilter[2] as string[])[0]
    : "";

  const handleStatusClick = (selectedStatus: string) => {
    // Remove existing status filter
    const updatedFilters = params.filters.filter(
      (filter) => filter[0] !== "status"
    );

    // Add new status filter if a status is selected
    if (selectedStatus) {
      updatedFilters.push(["status", "in", [selectedStatus]]);
    }

    setParams({
      ...params,
      filters: updatedFilters,
    });
  };

  // Get current SKU filter value
  const currentSkuFilter = params.filters.find((filter) => filter[0] === "sku");
  const currentSku = currentSkuFilter
    ? (currentSkuFilter[2] as string[])[0]
    : "";

  // Get SKU options from filterColumns
  const skuColumn = filterColumns.find((col) => col.name === "sku");
  const skuOptions = skuColumn?.unique_values || [];

  const handleSkuChange = (selectedSku: string) => {
    // Remove existing SKU filter
    const updatedFilters = params.filters.filter(
      (filter) => filter[0] !== "sku"
    );

    // Add new SKU filter if a SKU is selected
    if (selectedSku) {
      updatedFilters.push(["sku", "in", [selectedSku]]);
    }

    setParams({
      ...params,
      filters: updatedFilters,
    });
  };

  // Get current SKU name filter value
  const currentSkuNameFilter = params.filters.find(
    (filter) => filter[0] === "sku_name"
  );
  const currentSkuName = currentSkuNameFilter
    ? (currentSkuNameFilter[2] as string[])[0]
    : "";

  // Get SKU name options from filterColumns
  const skuNameColumn = filterColumns.find((col) => col.name === "sku_name");
  const skuNameOptions = skuNameColumn?.unique_values || [];

  const handleSkuNameChange = (selectedSkuName: string) => {
    // Remove existing SKU name filter
    const updatedFilters = params.filters.filter(
      (filter) => filter[0] !== "sku_name"
    );

    // Add new SKU name filter if a SKU name is selected
    if (selectedSkuName) {
      updatedFilters.push(["sku_name", "in", [selectedSkuName]]);
    }

    setParams({
      ...params,
      filters: updatedFilters,
    });
  };

  // Reporting attribution filter — a SKU-level reporting category that only
  // exists in po_plan_edit for clients whose data carries it (Brooklinen). Pure
  // presence: `reportingAttrColumn` is undefined for everyone else, so the
  // dropdown simply never renders for them — no customer hardcoding needed.
  const currentReportingAttrFilter = params.filters.find(
    (filter) => filter[0] === "reporting_attribution"
  );
  // Multi-select: the "in" filter holds the full list of chosen values.
  const currentReportingAttrs = currentReportingAttrFilter
    ? (currentReportingAttrFilter[2] as string[])
    : [];

  const reportingAttrColumn = filterColumns.find(
    (col) => col.name === "reporting_attribution"
  );
  // Real options (drop null/empty — they'd crash MultiSelect's lowercase), plus
  // a "(blank)" affordance to filter the unattributed rows when any exist.
  // apply_filters maps the "(blank)" value to a null/empty match.
  const rawReportingAttrValues = reportingAttrColumn?.unique_values || [];
  const reportingAttrOptions = [
    ...(rawReportingAttrValues.some((v) => !v) ? ["(blank)"] : []),
    ...rawReportingAttrValues.filter((v) => v),
  ];
  const showReportingAttr = !!reportingAttrColumn;

  const setReportingAttrFilter = (values: string[]) => {
    const updatedFilters = params.filters.filter(
      (filter) => filter[0] !== "reporting_attribution"
    );
    if (values.length > 0) {
      updatedFilters.push(["reporting_attribution", "in", values]);
    }
    setParams({
      ...params,
      filters: updatedFilters,
    });
  };

  // Get current location filter value
  const currentLocationFilter = params.filters.find(
    (filter) => filter[0] === "location"
  );
  const currentLocation = currentLocationFilter
    ? (currentLocationFilter[2] as string[])[0]
    : "";

  // Get location options from filterColumns
  const locationColumn = filterColumns.find((col) => col.name === "location");
  const locationOptions = locationColumn?.unique_values || [];

  const handleLocationChange = (selectedLocation: string) => {
    // Remove existing location filter
    const updatedFilters = params.filters.filter(
      (filter) => filter[0] !== "location"
    );

    // Add new location filter if a location is selected
    if (selectedLocation) {
      updatedFilters.push(["location", "in", [selectedLocation]]);
    }

    setParams({
      ...params,
      filters: updatedFilters,
    });
  };

  // Get current supplier filter value
  const currentSupplierFilter = params.filters.find(
    (filter) => filter[0] === "supplier"
  );
  const currentSupplier = currentSupplierFilter
    ? (currentSupplierFilter[2] as string[])[0]
    : "";

  // Get supplier options from filterColumns
  const supplierColumn = filterColumns.find((col) => col.name === "supplier");
  const supplierOptions = supplierColumn?.unique_values || [];

  const handleSupplierChange = (selectedSupplier: string) => {
    // Remove existing supplier filter
    const updatedFilters = params.filters.filter(
      (filter) => filter[0] !== "supplier"
    );

    // Add new supplier filter if a supplier is selected
    if (selectedSupplier) {
      updatedFilters.push(["supplier", "in", [selectedSupplier]]);
    }

    setParams({
      ...params,
      filters: updatedFilters,
    });
  };

  // Get current PO ID filter value
  const currentPoIdFilter = params.filters.find(
    (filter) => filter[0] === "purchase_order_id"
  );
  const currentPoId = currentPoIdFilter
    ? (currentPoIdFilter[2] as string[])[0]
    : "";

  // Get PO ID options from filterColumns
  const poIdColumn = filterColumns.find(
    (col) => col.name === "purchase_order_id"
  );
  const poIdOptions = poIdColumn?.unique_values || [];

  const handlePoIdChange = (selectedPoId: string) => {
    // Remove existing PO ID filter
    const updatedFilters = params.filters.filter(
      (filter) => filter[0] !== "purchase_order_id"
    );

    // Add new PO ID filter if a PO ID is selected
    if (selectedPoId) {
      updatedFilters.push(["purchase_order_id", "in", [selectedPoId]]);
    }

    setParams({
      ...params,
      filters: updatedFilters,
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        p: 1,
        pt: 0,
        flexWrap: "wrap",
        justifyContent: "space-between",
        width: "100%",
        minHeight: "55px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
        <ButtonGroup
          size="small"
          variant="outlined"
          sx={{ height: "32px", "& .MuiButton-root": { fontSize: 12 } }}
        >
          {availableStatuses.map((status) => {
            const countKey = `${sanitizeStatus(status)}_count`;
            const count: number = Number(statusCounts[countKey]) || 0;
            const label = STATUS_LABEL_MAP[status] ?? status;

            return (
              <Button
                key={status}
                onClick={() => handleStatusClick(status)}
                variant={currentStatus === status ? "contained" : "outlined"}
              >
                {label} ({count})
              </Button>
            );
          })}
        </ButtonGroup>
        {extraActions}
        {/* Export button removed; default header Download is now conditional */}
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          borderRadius: 1,
          alignItems: "flex-end",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 1, fontSize: 12 }}>
          {" "}
          {/* <SearchOutlined fontSize="small" sx={{ mt: 0.5 }} /> */}
          filters:
        </Box>
        <SearchableDropdown
          label={filterLabels.poId}
          selectedValue={String(currentPoId || "")}
          handleChange={handlePoIdChange}
          options={poIdOptions}
          isLoading={isLoading}
        />
        <SearchableDropdown
          label={filterLabels.sku}
          selectedValue={String(currentSku || "")}
          handleChange={handleSkuChange}
          options={skuOptions}
          isLoading={isLoading}
        />
        <SearchableDropdown
          label={filterLabels.skuName}
          selectedValue={String(currentSkuName || "")}
          handleChange={handleSkuNameChange}
          options={skuNameOptions}
          isLoading={isLoading}
        />
        {showReportingAttr && (
          <MultiSelect
            placeholder={filterLabels.reportingAttribution}
            searchable
            noFormatting
            options={reportingAttrOptions}
            selectedOptions={currentReportingAttrs}
            handleChange={(event: SelectChangeEvent<string[]>) => {
              const values = Array.isArray(event.target.value)
                ? event.target.value
                : [event.target.value];
              setReportingAttrFilter(values);
            }}
            handleDelete={(option: string) =>
              setReportingAttrFilter(
                currentReportingAttrs.filter((v) => v !== option)
              )
            }
            isLoading={isLoading}
            sx={{ minWidth: 180 }}
          />
        )}
        <SearchableDropdown
          label={filterLabels.location}
          selectedValue={String(currentLocation || "")}
          handleChange={handleLocationChange}
          options={locationOptions}
          isLoading={isLoading}
        />
        <SearchableDropdown
          label={filterLabels.supplier}
          selectedValue={String(currentSupplier || "")}
          handleChange={handleSupplierChange}
          options={supplierOptions}
          isLoading={isLoading}
        />
      </Box>
    </Box>
  );
};
